import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon, colors } from "../ui/index.js";
/**
 * Per-court "booking type" config keyed by slot origin. `rule` is the
 * common/default case so it reads as a muted/secondary tone; the exceptions
 * (`reconciled`, `manual`) are slightly more prominent. Missing origin is
 * treated as "rule".
 *
 * The origin is shown as a small ICON marker INSIDE the court chip (not a
 * detached label) — see {@link CourtChip}. {@link OriginLegend} documents what
 * each marker means (always-visible key, so it works on touch without hover).
 *
 * Single source of truth for the origin marker — reused by the sessions LIST
 * row, the session DETAIL page, and the admin sessions tab.
 */
export const ORIGIN_BADGE = {
    rule: {
        label: "Auto",
        icon: "zap",
        color: colors.textTertiary,
        desc: "booked automatically by a recurring rule",
    },
    reconciled: {
        label: "Synced",
        icon: "git-merge",
        color: colors.info,
        desc: "found already booked at the venue and matched to this session",
    },
    manual: {
        label: "Manual",
        icon: "user",
        color: colors.accent,
        desc: "added by hand, not from a rule",
    },
};
/** Hover/press tooltip text for an origin marker, e.g. "Manual — added by hand…". */
export function originTitle(origin) {
    const b = ORIGIN_BADGE[origin];
    return `${b.label} — ${b.desc}`;
}
function tonePalette(tone) {
    switch (tone) {
        case "booked":
            return { bg: colors.successLight, fg: colors.success, border: colors.success };
        case "ineligible":
            return { bg: colors.surface, fg: colors.danger, border: colors.danger };
        default:
            return { bg: colors.surfaceSecondary, fg: colors.textTertiary, border: colors.border };
    }
}
function hhmm(iso) {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
/** Booked courts first, then by court number. */
function sortCourts(courts) {
    return [...courts].sort((a, b) => {
        if (a.status !== b.status)
            return a.status === "booked" ? -1 : 1;
        return (a.courtNumber ?? 99) - (b.courtNumber ?? 99);
    });
}
/**
 * A single court chip with the origin marker rendered INSIDE the chip — a small
 * icon carrying a hover/press tooltip. The court number stays the primary text.
 * `title` is the chip-level tooltip (e.g. a booking reference or an ineligible
 * reason); the marker has its own origin tooltip.
 */
export function CourtChip({ courtNumber, origin, tone = "planned", title, }) {
    const known = courtNumber !== undefined;
    const o = origin ?? "rule";
    const badge = ORIGIN_BADGE[o];
    const p = tonePalette(tone);
    return (_jsxs("span", { title: title, style: {
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "3px 9px",
            borderRadius: 6,
            fontSize: "0.78rem",
            fontWeight: 600,
            background: p.bg,
            color: p.fg,
            border: `1px solid ${p.border}`,
            fontVariantNumeric: "tabular-nums",
            textDecoration: tone === "ineligible" && !known ? "line-through" : undefined,
            opacity: tone === "ineligible" && !known ? 0.85 : 1,
        }, children: [_jsx("span", { title: originTitle(o), "aria-label": badge.label, style: { display: "inline-flex", alignItems: "center", cursor: "help" }, children: _jsx(Icon, { name: badge.icon, size: 12 }) }), known ? `Court ${courtNumber}` : "Court —"] }));
}
/**
 * The KEY for court-chip origin markers. Always visible so the meaning is clear
 * without hovering/pressing each marker (important on touch). Render once per
 * page that shows court chips.
 */
export function OriginLegend({ style }) {
    const origins = ["rule", "reconciled", "manual"];
    return (_jsxs("div", { style: {
            display: "flex",
            flexDirection: "column",
            gap: 5,
            fontSize: "0.72rem",
            color: colors.textSecondary,
            ...style,
        }, children: [_jsx("span", { style: {
                    fontWeight: 700,
                    color: colors.textTertiary,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                }, children: "Key" }), _jsx(LegendSubhead, { children: "Type" }), origins.map((o) => {
                const b = ORIGIN_BADGE[o];
                return (_jsxs("div", { title: originTitle(o), style: { display: "flex", alignItems: "flex-start", gap: 6 }, children: [_jsx("span", { style: { display: "inline-flex", marginTop: 1, flexShrink: 0 }, children: _jsx(Icon, { name: b.icon, size: 12, color: b.color }) }), _jsxs("span", { style: { lineHeight: 1.35 }, children: [_jsx("strong", { style: { color: colors.text, fontWeight: 600 }, children: b.label }), " — ", b.desc] })] }, o));
            }), _jsx(LegendSubhead, { children: "Court status" }), STATUS_LEGEND.map((s) => {
                const p = tonePalette(s.tone);
                return (_jsxs("div", { style: { display: "flex", alignItems: "flex-start", gap: 6 }, children: [_jsx("span", { "aria-hidden": true, style: {
                                width: 13,
                                height: 13,
                                borderRadius: 4,
                                background: p.bg,
                                border: `1px solid ${p.border}`,
                                flexShrink: 0,
                                marginTop: 1,
                            } }), _jsxs("span", { style: { lineHeight: 1.35 }, children: [_jsx("strong", { style: { color: colors.text, fontWeight: 600 }, children: s.label }), " — ", s.desc] })] }, s.label));
            })] }));
}
const STATUS_LEGEND = [
    { tone: "booked", label: "Booked", desc: "confirmed at the venue" },
    { tone: "planned", label: "Pending", desc: "waiting to be booked" },
    { tone: "ineligible", label: "Unbookable", desc: "can't be booked under your current windows" },
];
function LegendSubhead({ children }) {
    return (_jsx("span", { style: {
            fontWeight: 700,
            fontSize: "0.64rem",
            color: colors.textTertiary,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginTop: 2,
        }, children: children }));
}
/**
 * A small, LIGHT court chip for the list timeline — origin icon + "Court N" in
 * small text (no heavy border). Tints green when booked, muted when pending.
 * The icon inherits the chip colour.
 */
function listChip(c, i) {
    const known = c.courtNumber !== undefined;
    const o = c.origin ?? "rule";
    return (_jsxs("span", { title: originTitle(o), style: {
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            fontSize: "0.72rem",
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 5,
            background: known ? colors.successLight : colors.surfaceSecondary,
            color: known ? colors.success : colors.textTertiary,
            fontVariantNumeric: "tabular-nums",
        }, children: [_jsx(Icon, { name: ORIGIN_BADGE[o].icon, size: 10 }), known ? `Court ${c.courtNumber}` : "Court —"] }, `${c.courtNumber ?? "?"}-${c.startTime ?? ""}-${i}`));
}
/**
 * A LIGHT mini-timeline preview of a session's courts for a list row: each
 * distinct start time is its own LINE (small time label + the courts booked in
 * it), so a multi-timeslot session reads top-to-bottom as a timeline instead of
 * a flat "Court 3 Court 3 Court 4 …" list. Chips stay small (origin icon +
 * "Court N", small text); the full detail + origin Key live on the drilldown.
 * Single timeslot → one line of chips, no time label.
 */
export function CourtTimeline({ courts, style, }) {
    if (courts.length === 0)
        return null;
    const groups = new Map();
    for (const c of courts) {
        const key = c.startTime ?? "";
        const arr = groups.get(key) ?? [];
        arr.push(c);
        groups.set(key, arr);
    }
    const keys = [...groups.keys()].sort();
    const showTimes = keys.filter((k) => k !== "").length > 1;
    return (_jsx("div", { style: { display: "flex", flexDirection: "column", gap: 3, ...style }, children: keys.map((k) => (_jsxs("div", { style: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5 }, children: [showTimes && k && (_jsx("span", { style: {
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        color: colors.textTertiary,
                        fontVariantNumeric: "tabular-nums",
                        minWidth: 40,
                    }, children: hhmm(k) })), sortCourts(groups.get(k) ?? []).map((c, i) => listChip(c, i))] }, k || "untimed"))) }));
}
